import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { RotateCounterClockwise } from "@acto/web-common/components";
import { Card } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import type { HideReasonKind } from "./ReasonPrompt";

const Frame = styled(Card)(({ theme }) => ({
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `${theme.fieldSalesTwo.spacing[32]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

const Title = styled("p")(({ theme }) => ({
  margin: 0,
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["17"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

const Hint = styled("p")(({ theme }) => ({
  margin: 0,
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["13"],
  fontWeight: theme.fieldSalesTwo.font.weight["400"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const UndoButton = styled(GlassButton)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[12],
  height: theme.fieldSalesTwo.spacing[50],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "& svg": { width: "20px", height: "auto", flexShrink: 0 },
}));

interface HiddenCardNoticeProps {
  cardLabel: string;
  reason: HideReasonKind;
  onUndo: () => void;
}

/** Card-hide confirmation with undo; the hint follows the reason. */
export function HiddenCardNotice({ cardLabel, reason, onUndo }: HiddenCardNoticeProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.cardHide");

  return (
    <Frame role="status">
      <Title>{t("hiddenNotice", { label: cardLabel })}</Title>
      <Hint>{reason === "dataIncorrect" ? t("hiddenHintDataIncorrect") : t("hiddenHintNotRelevant")}</Hint>
      <UndoButton type="button" onClick={onUndo}>
        <RotateCounterClockwise aria-hidden="true" />
        {t("undo")}
      </UndoButton>
    </Frame>
  );
}
